import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Inbox, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/messages`;

// Coach-facing inbox for the dashboard. Two-pane: conversation list left,
// thread + reply composer right.
export default function CoachInbox({ coachEmail }) {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null); // conversation object
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const loadInbox = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/inbox`, { params: { coach_email: coachEmail } });
      setConvs(r.data.conversations || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load inbox.");
    } finally { setLoading(false); }
  };

  const openThread = async (c) => {
    setActive(c);
    try {
      const r = await axios.get(`${API}/thread`, { params: { coach_slug: c.coach_slug, client_email: c.client_email } });
      setThread(r.data.messages || []);
      await axios.post(`${API}/read`, { reader_type: "coach", reader_email: coachEmail, coach_slug: c.coach_slug, client_email: c.client_email });
      // refresh unread badges
      loadInbox();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't open conversation.");
    }
  };

  useEffect(() => { loadInbox(); /* eslint-disable-next-line */ }, [coachEmail]);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [thread]);

  const reply = async (e) => {
    e.preventDefault();
    if (!active || !body.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/coach-reply`, {
        coach_email: coachEmail, client_email: active.client_email, body: body.trim(),
      });
      setThread((prev) => [...prev, r.data.message]);
      setBody("");
      loadInbox();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send.");
    } finally { setBusy(false); }
  };

  const totalUnread = convs.reduce((n, c) => n + (c.unread_count || 0), 0);

  return (
    <div className="mt-10" data-testid="coach-inbox">
      <div className="flex items-baseline justify-between">
        <h2 className="font-display font-medium text-2xl tracking-tight text-slate-900 inline-flex items-center gap-2">
          <Inbox className="w-5 h-5 text-slate-500" strokeWidth={2} /> Messages
          {totalUnread > 0 && (
            <span data-testid="coach-inbox-unread" className="ml-1 inline-flex items-center h-5 min-w-5 px-1.5 rounded-full bg-[#2563EB] text-white text-[10.5px] font-semibold">
              {totalUnread}
            </span>
          )}
        </h2>
        <span className="text-[12.5px] text-slate-500">{convs.length} conversation{convs.length === 1 ? "" : "s"}</span>
      </div>

      <div className="mt-4 grid md:grid-cols-[320px_1fr] gap-4 min-h-[420px]">
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-6 text-[13px] text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…</div>
          ) : convs.length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare className="w-6 h-6 mx-auto text-slate-300" strokeWidth={1.5} />
              <p className="mt-3 text-[13px] text-slate-500">No messages yet.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {convs.map((c) => (
                <li key={c.conversation_key}>
                  <button
                    onClick={() => openThread(c)}
                    data-testid={`coach-inbox-conv-${c.client_email.replace(/[@.]/g, "-")}`}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition-colors ${
                      active?.conversation_key === c.conversation_key ? "bg-slate-50" : ""
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-slate-100 grid place-items-center text-slate-500 text-[11px] font-semibold">
                      {(c.client_name || c.client_email).slice(0, 1).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-medium text-[13.5px] text-slate-900 truncate">{c.client_name || c.client_email}</div>
                        {c.unread_count > 0 && (
                          <span className="inline-flex h-5 min-w-5 px-1.5 items-center justify-center rounded-full bg-[#2563EB] text-white text-[10.5px] font-semibold">{c.unread_count}</span>
                        )}
                      </div>
                      <p className="mt-0.5 text-[12.5px] text-slate-500 truncate">{c.last_message.body}</p>
                      <div className="text-[10.5px] text-slate-400 mt-0.5">{new Date(c.last_message.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl flex flex-col overflow-hidden">
          {!active ? (
            <div className="flex-1 grid place-items-center text-[13px] text-slate-500">Pick a conversation on the left</div>
          ) : (
            <>
              <div className="p-4 border-b border-slate-100">
                <div className="font-medium text-[14px] text-slate-900">{active.client_name || active.client_email}</div>
                <div className="text-[12px] text-slate-500">{active.client_email}</div>
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50">
                {thread.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_type === "coach" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-[13.5px] leading-relaxed ${
                      m.sender_type === "coach" ? "bg-[#2563EB] text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
                    }`}>
                      <div>{m.body}</div>
                      <div className={`mt-1 text-[10.5px] ${m.sender_type === "coach" ? "text-white/60" : "text-slate-400"}`}>
                        {new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={reply} className="border-t border-slate-100 p-3 flex gap-2 bg-white">
                <input
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  data-testid="coach-reply-body"
                  placeholder="Reply…"
                  maxLength={2000}
                  className="flex-1 h-11 rounded-full bg-slate-50 border border-slate-200 px-4 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/25"
                />
                <button type="submit" disabled={busy || !body.trim()} data-testid="coach-reply-send" className="h-11 w-11 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white grid place-items-center transition-colors disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
